import React, { useState, useEffect } from "react";
import Sidebar from '../Sidebar/SidebarAdmin';
import { database } from "../../firebase";
import { ref, get, child } from 'firebase/database';
import {
  Box,
  Typography,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from "@mui/material";
import { FaDownload } from 'react-icons/fa';
import * as XLSX from 'xlsx';

// Utility to export table to Excel
const exportTableToExcel = (tableID, fileName) => {
  const table = document.getElementById(tableID);
  const workbook = XLSX.utils.table_to_book(table, { sheet: "Sheet1" });
  XLSX.writeFile(workbook, fileName + ".xlsx");
};

const AdminReports = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const sidebarWidth = isSidebarOpen ? 250 : 80;
  const toggleSidebar = () => setIsSidebarOpen(prev => !prev);

  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);

  // Export dialog state
  const [exportModal, setExportModal] = useState({ isOpen: false, tableID: '', defaultName: '' });
  const [reportName, setReportName] = useState('');
  const [previewHTML, setPreviewHTML] = useState('');

  useEffect(() => {
    const dbRef = ref(database);
    get(child(dbRef, '/'))
      .then(snapshot => {
        if (snapshot.exists()) setData(snapshot.val());
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setError('Failed to fetch data.');
        setLoading(false);
      });
  }, []);

  const todaySuffix = new Date().toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
    .replace(/ /g,'').replace(/,/g,'');

  const openExport = (tableID, prefix) => {
    const defaultName = `${prefix}(${todaySuffix})`;
    setExportModal({ isOpen: true, tableID, defaultName });
    setReportName(defaultName);
    const table = document.getElementById(tableID);
    setPreviewHTML(table ? table.outerHTML : '');
  };

  const closeExport = () => setExportModal({ isOpen: false, tableID: '', defaultName: '' });
  const handleConfirmExport = () => {
    exportTableToExcel(exportModal.tableID, reportName);
    closeExport();
  };

  if (loading) return <Box sx={{ display:'flex', justifyContent:'center', alignItems:'center', height:'100vh' }}><Typography>Loading...</Typography></Box>;
  if (error) return <Box sx={{ p:3 }}><Typography color="error">{error}</Typography></Box>;

  // Data preparations
  const sessionPayments = Object.entries(data.payments || {}).map(([id, p]) => ({
    id,
    user: p.user_email,
    amount: p.amount,
    minutes: p.minutes,
    timestamp: p.timestamp,
  }));

  const coinboxHistory = Object.entries(data.coinboxhistory || {}).map(([id, h]) => ({
    id,
    time: h.timeflushed,
    rental: `${h.sum.rental_coins}`,
    extend: `${h.sum.extend_coins} `,
    penalty: `${h.sum.penalty_coins}`,
    total: `${h.sum.total_coins} `,
  }));

  const coins = data.coinbox?.coins || {};
  const coinboxSummary = [
    { metric:'Rental Coins', count:coins.rentalcoincount, value:coins.rentalcoinvalue },
    { metric:'Extend Coins', count:coins.totalextendcoin, value:coins.totalextendvalue },
    { metric:'Penalty Coins', count:coins.totalpenaltycoin, value:coins.totalpenaltyvalue },
    { metric:'Total Coins', count:coins.rentalcoincount + coins.totalextendcoin + coins.totalpenaltycoin, value:coins.rentalcoinvalue + coins.totalextendvalue + coins.totalpenaltyvalue },
  ];

  const perLocker = Object.entries(data.coinbox?.lockers || {}).map(([locker, stats]) => ({
    locker,
    rental: `${stats.rentalcoincount} / ${stats.rentalcoinvalue}`,
    extend: `${stats.extendcoin} / ${stats.extendvalue}`,
    penalty: `${stats.penaltycoin} / ${stats.penaltyvalue}`,
    total: `${stats.rentalcoincount + stats.extendcoin + stats.penaltycoin} / ${stats.rentalcoinvalue + stats.extendvalue + stats.penaltyvalue}`,
  }));

  const commonCell = { py:{ xs:0.5, sm:1 }, px:1, textAlign:'center', whiteSpace:'normal', wordBreak:'break-word' };

  const tabConfig = [
    { label:'Session Payments', id:'sessionPaymentsTable' },
    { label:'Coinbox History', id:'coinboxHistoryTable' },
    { label:'Coinbox Summary', id:'coinboxSummaryTable' },
    { label:'Per-Locker Totals', id:'perLockerTable' }
  ];

  return (
    <Box sx={{ backgroundColor:'#f7f9fc', minHeight:'100vh', pt:8, position:'relative' }}>
      <Sidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <Box sx={{ position:'fixed', top:'64px', left:{ xs:'8px', sm:`calc(${sidebarWidth}px + 16px)` }, right:{ xs:'8px', sm:'16px' }, bottom:'16px', backgroundColor:'#cfe8fc', borderRadius:2, p:{ xs:2, sm:3 }, overflow:'auto' }}>
        <Typography variant="h5" sx={{ fontWeight:'bold', textAlign:'center', mb:2, fontSize:{ xs:'1.25rem', sm:'1.75rem'} }}>Admin Reports</Typography>

        <Stack direction='row' spacing={1} alignItems='center' sx={{ overflowX:'auto', mb:2 }}>
          {tabConfig.map((t,i) => (
            <Button
              key={t.id}
              size='small'
              onClick={() => setTab(i)}
              variant={tab===i ? 'contained' : 'text'}
            >
              {t.label}
            </Button>
          ))}
          <IconButton onClick={() => openExport(tabConfig[tab].id, tabConfig[tab].label.replace(/ /g,''))}>
            <FaDownload />
          </IconButton>
        </Stack>

        {/* Session Payments Table */}
        {tab===0 && (
          <TableContainer component={Paper} id='sessionPaymentsTable' sx={{ boxShadow:'none' }}>
            <Table size="small" stickyHeader sx={{ tableLayout:'fixed', width:'100%', }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize:{ xs:'0.75rem', sm:'1rem', textAlign: 'center'} }}>Payment ID</TableCell>
                  <TableCell sx={{ fontSize:{ xs:'0.75rem', sm:'1rem',  textAlign: 'center'} }}>User</TableCell>
                  <TableCell sx={{ fontSize:{ xs:'0.75rem', sm:'1rem',  textAlign: 'center'} }}>Amount (₱)</TableCell>
                  <TableCell sx={{ fontSize:{ xs:'0.75rem', sm:'1rem', textAlign: 'center'} }}>Minutes</TableCell>
                  <TableCell sx={{ fontSize:{ xs:'0.75rem', sm:'1rem',  textAlign: 'center'} }}>Timestamp</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sessionPayments.map(s => (
                  <TableRow key={s.id} hover>
                    <TableCell sx={commonCell}>{s.id}</TableCell>
                    <TableCell sx={commonCell}>{s.user}</TableCell>
                    <TableCell sx={commonCell}>{s.amount}</TableCell>
                    <TableCell sx={commonCell}>{s.minutes}</TableCell>
                    <TableCell sx={commonCell}>{new Date(s.timestamp).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Coinbox History Table */}
        {tab===1 && (
          <TableContainer component={Paper} id='coinboxHistoryTable' sx={{ boxShadow:'none' }}>
            <Table size="small" stickyHeader sx={{ tableLayout:'fixed', width:'100%', }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize:{ xs:'0.75rem', sm:'1rem', textAlign: 'center'} }}>Flushed On</TableCell>
                  <TableCell sx={{ fontSize:{ xs:'0.75rem', sm:'1rem',  textAlign: 'center'} }}>Rental</TableCell>
                  <TableCell sx={{ fontSize:{ xs:'0.75rem', sm:'1rem', textAlign: 'center'} }}>Extend</TableCell>
                  <TableCell sx={{ fontSize:{ xs:'0.75rem', sm:'1rem', textAlign: 'center'} }}>Penalty</TableCell>
                  <TableCell sx={{ fontSize:{ xs:'0.75rem', sm:'1rem',  textAlign: 'center'} }}>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {coinboxHistory.map(c => (
                  <TableRow key={c.id} hover>
                    <TableCell sx={commonCell}>{new Date(c.time).toLocaleString()}</TableCell>
                    <TableCell sx={commonCell}>{c.rental}</TableCell>
                    <TableCell sx={commonCell}>{c.extend}</TableCell>
                    <TableCell sx={commonCell}>{c.penalty}</TableCell>
                    <TableCell sx={commonCell}>{c.total}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Coinbox Summary Table */}
        {tab===2 && (
          <TableContainer component={Paper} id='coinboxSummaryTable' sx={{ boxShadow:'none', maxWidth:400, mx:'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell  sx={{ fontSize: { xs: '0.75rem', sm: '1rem' }, textAlign: 'center' }}>Metric</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '1rem' }, textAlign: 'center' }}>Count</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '1rem' }, textAlign: 'center' }}>Value (₱)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {coinboxSummary.map(item => (
                  <TableRow key={item.metric} hover>
                    <TableCell sx={commonCell}>{item.metric}</TableCell>
                    <TableCell sx={commonCell}>{item.count}</TableCell>
                    <TableCell sx={commonCell}>{item.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Per-Locker Totals Table */}
        {tab===3 && (
          <TableContainer component={Paper} id='perLockerTable' sx={{ boxShadow:'none' }}>
            <Table size="small" stickyHeader sx={{ tableLayout:'fixed', width:'100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize:{ xs:'0.75rem', sm:'1rem', textAlign: 'center'} }}>Locker</TableCell>
                  <TableCell sx={{ fontSize:{ xs:'0.75rem', sm:'1rem', textAlign: 'center'} }}>Rental</TableCell>
                  <TableCell sx={{ fontSize:{ xs:'0.75rem', sm:'1rem', textAlign: 'center'} }}>Extend</TableCell>
                  <TableCell sx={{ fontSize:{ xs:'0.75rem', sm:'1rem', textAlign: 'center'} }}>Penalty</TableCell>
                  <TableCell sx={{ fontSize:{ xs:'0.75rem', sm:'1rem', textAlign: 'center'} }}>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {perLocker.map(l => (
                  <TableRow key={l.locker} hover>
                    <TableCell sx={commonCell}>{l.locker}</TableCell>
                    <TableCell sx={commonCell}>{l.rental}</TableCell>
                    <TableCell sx={commonCell}>{l.extend}</TableCell>
                    <TableCell sx={commonCell}>{l.penalty}</TableCell>
                    <TableCell sx={commonCell}>{l.total}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Export Preview Modal */}
        <Dialog open={exportModal.isOpen} onClose={closeExport} fullWidth>
          <DialogTitle>Export Preview</DialogTitle>
          <DialogContent>
            <TextField
              label="Report Name"
              value={reportName}
              onChange={e => setReportName(e.target.value)}
              fullWidth
              sx={{ mb:2 }}
            />
            <div dangerouslySetInnerHTML={{ __html: previewHTML }} style={{ overflowX:'auto' }} />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeExport}>Cancel</Button>
            <Button variant="contained" onClick={handleConfirmExport}>Export</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default AdminReports;
